import React, { useState, useEffect } from "react";
import { useTranslation } from "../../hooks/useTranslation";

const Calendar = ({
  selectedStartDate,
  selectedEndDate,
  onDateSelect,
  reservedDates = [],
  dailyPrices = {},
  // Minimum nights between pickup and return.
  minStay = 2,
  // Dates where an existing booking ends early enough for the van to go back
  // out the same afternoon. They can start a new booking, but never end one:
  // the night before belongs to the booking that is being returned.
  turnaroundDates = [],
}) => {
  const { t } = useTranslation('camperDetails');

  // Initialize currentMonth to selected date's month if available, otherwise current month
  const getInitialMonth = () => {
    if (selectedEndDate) {
      return new Date(selectedEndDate.getFullYear(), selectedEndDate.getMonth(), 1);
    }
    if (selectedStartDate) {
      return new Date(selectedStartDate.getFullYear(), selectedStartDate.getMonth(), 1);
    }
    return new Date();
  };

  const [currentMonth, setCurrentMonth] = useState(getInitialMonth());
  const [isSelectingEnd, setIsSelectingEnd] = useState(false);

  // Update currentMonth when selectedEndDate changes (user navigated to a future month and selected a date)
  useEffect(() => {
    if (selectedEndDate) {
      const selectedMonth = new Date(selectedEndDate.getFullYear(), selectedEndDate.getMonth(), 1);
      setCurrentMonth(selectedMonth);
    } else if (selectedStartDate) {
      const selectedMonth = new Date(selectedStartDate.getFullYear(), selectedStartDate.getMonth(), 1);
      setCurrentMonth(selectedMonth);
    }
  }, [selectedStartDate, selectedEndDate]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Use provided reserved dates directly
  const reserved = reservedDates;
  const turnaround = turnaroundDates;

  const isTurnaround = (date) => {
    if (!date) return false;
    return turnaround.includes(formatDateForComparison(date));
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const formatDateForComparison = (date) => {
    // Use local timezone to avoid date shifting issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const isReserved = (date) => {
    if (!date) return false;
    const formattedDate = formatDateForComparison(date);
    return reserved.includes(formattedDate);
  };

  const isPast = (date) => {
    if (!date) return false;
    return date < today;
  };

  const getDayPrice = (date) => {
    if (!date) return 0;
    const dateStr = formatDateForComparison(date);
    
    // Use provided daily prices directly
    if (Object.keys(dailyPrices).length > 0) {
      return dailyPrices[dateStr] || 0;
    }
    
    // Fallback to simple weekend/weekday logic if no pricing data
    const dayOfWeek = date.getDay();
    if (dayOfWeek === 5 || dayOfWeek === 6 || dayOfWeek === 0) {
      return 95; // Weekend price
    }
    return 75; // Weekday price
  };

  const isInRange = (date) => {
    if (!date || !selectedStartDate) return false;
    if (!selectedEndDate) return false;
    return date >= selectedStartDate && date <= selectedEndDate;
  };

  // A day the user can act on at all: it just has to be free and not in the
  // past. This is deliberately independent of the range being built, so
  // picking a start date never makes other free days go dead.
  const isSelectable = (date) => {
    if (!date) return false;
    return !isPast(date) && !isReserved(date);
  };

  // True when an existing booking sits between the start and this date, which
  // would make the range span someone else's reservation. Turnaround days count
  // as blocked here too: the van is out overnight on the booking being
  // returned, so a range cannot run through one.
  const hasReservedBetween = (start, end) => {
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      if (d.getTime() === start.getTime()) continue;
      if (isReserved(d) || isTurnaround(d)) return true;
    }
    return false;
  };

  // Whether clicking this date would close the current range. Only meaningful
  // while a start is chosen and the return date is still open.
  const isValidRangeEnd = (date) => {
    if (!isSelectable(date)) return false;
    if (!selectedStartDate || selectedEndDate) return false;
    if (date < selectedStartDate) return false;
    // A turnaround day can only open a booking, never close one — the previous
    // customer still has the van the night before.
    if (isTurnaround(date)) return false;

    const daysDiff = Math.ceil(
      (date - selectedStartDate) / (1000 * 60 * 60 * 24),
    );
    if (daysDiff < minStay) return false;

    return !hasReservedBetween(new Date(selectedStartDate), new Date(date));
  };

  const handleDateClick = (date) => {
    if (!isSelectable(date)) return;

    // Nothing chosen yet, or a complete range: begin a new selection.
    if (!selectedStartDate || selectedEndDate) {
      onDateSelect(date, null);
      setIsSelectingEnd(true);
      return;
    }

    // A start is set and we're waiting for the return date.
    if (isValidRangeEnd(date)) {
      onDateSelect(selectedStartDate, date);
      setIsSelectingEnd(false);
      return;
    }

    // Any other free day — earlier than the start, or cut off from it by a
    // reservation — becomes the new start rather than a click that does
    // nothing.
    onDateSelect(date, null);
    setIsSelectingEnd(true);
  };

  const clearSelection = () => {
    onDateSelect(null, null);
    setIsSelectingEnd(false);
  };

  const nextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.setMonth(currentMonth.getMonth() + 1)),
    );
  };

  const prevMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.setMonth(currentMonth.getMonth() - 1)),
    );
  };

  const monthNames = t('calendar.months', { returnObjects: true });

  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const days = getDaysInMonth(currentMonth);

  return (
    <div className="calendar-container">
      <div className="calendar-header">
        <button
          className="calendar-nav-btn"
          onClick={prevMonth}
          disabled={
            currentMonth.getMonth() === today.getMonth() &&
            currentMonth.getFullYear() === today.getFullYear()
          }
        >
          &#8249;
        </button>
        <h3 className="calendar-month">
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </h3>
        <button className="calendar-nav-btn" onClick={nextMonth}>
          &#8250;
        </button>
      </div>

      <div className="calendar-grid">
        {dayNames.map((day) => (
          <div key={day} className="calendar-day-header">
            {day}
          </div>
        ))}

        {days.map((date, index) => {
          if (!date) {
            return <div key={index} className="calendar-day empty"></div>;
          }

          const isStart =
            selectedStartDate && date.getTime() === selectedStartDate.getTime();
          const isEnd =
            selectedEndDate && date.getTime() === selectedEndDate.getTime();
          const inRange = isInRange(date);
          const selectable = isSelectable(date);
          const reserved = isReserved(date);
          const turnaroundDay = isTurnaround(date);
          const past = isPast(date);
          const price = getDayPrice(date);

          let className = "calendar-day";
          if (past) className += " past";
          if (reserved) className += " reserved";
          if (turnaroundDay && !reserved && !past) className += " turnaround";
          if (isStart) className += " selected start";
          if (isEnd) className += " selected end";
          if (inRange && !isStart && !isEnd) className += " in-range";
          if (selectable && !past && !reserved) className += " selectable";

          return (
            <div
              key={index}
              className={className}
              onClick={() => handleDateClick(date)}
              title={turnaroundDay ? t('calendar.turnaroundHint') : undefined}
            >
              <div className="calendar-day-number">{date.getDate()}</div>
              {selectable && !past && !reserved && (
                <div className="calendar-day-price">€{price}</div>
              )}
            </div>
          );
        })}
      </div>

      <div className="calendar-legend">
        <div className="legend-item">
          <div className="legend-color available"></div>
          <span>{t('calendar.available')}</span>
        </div>
        <div className="legend-item">
          <div className="legend-color reserved"></div>
          <span>{t('calendar.reserved')}</span>
        </div>
        <div className="legend-item">
          <div className="legend-color turnaround"></div>
          <span>{t('calendar.turnaround')}</span>
        </div>
        <div className="legend-item">
          <div className="legend-color selected"></div>
          <span>{t('calendar.selected')}</span>
        </div>
      </div>

      {selectedStartDate && (
        <div className="calendar-hint">
          <span>
            {isSelectingEnd
              ? minStay > 0
                ? t('calendar.selectReturnMin', { nights: minStay })
                : t('calendar.selectReturn')
              : t('calendar.datesSelected')}
          </span>
          <button
            type="button"
            className="calendar-clear-btn"
            onClick={clearSelection}
          >
            {t('calendar.clearDates')}
          </button>
        </div>
      )}
    </div>
  );
};

export default Calendar;
